import { ref, reactive, computed } from '@vue/composition-api'
import { login as loginService, getGoogleOAuthUrl, googleOAuthTokenValidation, getMicrosoftOAuthUrl, microsoftOAuthTokenValidation, logout as logoutService } from '@/app/authentication/Services'
import ServerIsDown from '@/app/shared/exceptions/ServerIsDown'
import webSocketService from '@/services/WebSocketService'
import router from '@/router'
import store from '@/store'
import usePostLoginInit from '@/app/authentication/compositions/UsePostLoginInit'
import useDeviceDetection from '@/app/authentication/compositions/UseDeviceDetection'
import { clearSalesStatusShown } from '@/app/shared/salesStatusSession'
import * as Sentry from '@sentry/vue'

export default function useAuthentication () {
  const isLoading = ref(false)
  const loginErrorMessage = ref(null)
  const forceLogoutMessage = ref(null)

  const auth = reactive({
    email: '',
    password: ''
  })

  const isThereErrorLoginMessage = computed(() => {
    return loginErrorMessage.value !== null
  })

  const isThereForceLogoutMessage = computed(() => {
    return forceLogoutMessage.value !== null
  })

  // Initialize post-login composition
  const { performPostLoginInit } = usePostLoginInit(store, router)
  const { isMobile, isDesktop } = useDeviceDetection()

  // ✅ CENTRALIZED LOGIN COMPLETION - SINGLE SOURCE OF TRUTH
  const completeLogin = async (authResponse, loginType = 'normal', deviceInfo = {}) => {
    // Cada inicio de sesión debe volver a mostrar el estado de ventas, aunque se
    // reutilice la misma pestaña (sessionStorage sobrevive al logout).
    clearSalesStatusShown()

    try {
      // Use provided device info or get from composition scope
      const currentIsMobile = deviceInfo.isMobile || isMobile.value
      const currentIsDesktop = deviceInfo.isDesktop || isDesktop.value

      // Perform complete post-login initialization
      const initResult = await performPostLoginInit(currentIsMobile, currentIsDesktop)

      // Handle routing based on initialization result and device type
      if (initResult.shouldShowMobileWelcome && loginType === 'normal') {
        // Only show mobile welcome for normal login, skip for SSO
        return { shouldShowMobileWelcome: true }
      } else if (authResponse.regions && authResponse.regions.length) {
        // Handle region selection if needed
        router.push({ name: 'regionSelection', params: { regions: authResponse.regions } })
      } else if (initResult.routeTo && currentIsDesktop) {
        // Route to user's default view
        router.push({ name: initResult.routeTo })
      } else {
        // Fallback to Dashboard
        router.push({ name: 'Dashboard' })
      }

      return { success: true, shouldShowMobileWelcome: false }
    } catch (error) {
      console.error('❌ Error during centralized login completion:', error)

      // Fallback routing if initialization fails
      if (authResponse.regions && authResponse.regions.length) {
        router.push({ name: 'regionSelection', params: { regions: authResponse.regions } })
      } else {
        router.push({ name: 'Dashboard' })
      }

      throw error
    }
  }

  // Alias for regular login to match Login.vue expectations
  const login = async (email, password) => {
    try {
      isLoading.value = true
      loginErrorMessage.value = null

      const response = await loginService(email.toLowerCase().trim(), String(password))

      // Store auth token and connect websocket
      localStorage.setItem('accessToken', response.access_token)
      localStorage.setItem('refreshToken', response.refresh_token || '')
      if (response.user_id) {
        webSocketService.connect(response.user_id)
      }

      return response
    } catch (responseError) {
      // Handle different error structures from api-client.js
      // For 403 errors: the full axios error object is rejected
      // For other errors: only error.response is rejected
      const status = responseError.status || responseError.response?.status
      const errorData = responseError.data || responseError.response?.data

      // Check if this is a server down error (no status at all)
      if (!status) {
        throw new ServerIsDown('Servidor fuera de servicio')
      }

      // For all errors, show the API error message
      throw new Error(errorData?.error || 'Error al iniciar sesión')
    } finally {
      isLoading.value = false
    }
  }

  // Legacy method for backwards compatibility
  const performLogin = async (store, router) => {
    try {
      isLoading.value = true
      loginErrorMessage.value = null

      const response = await loginService(auth.email.toLowerCase().trim(), String(auth.password))

      // Store auth token and connect websocket
      localStorage.setItem('accessToken', response.access_token)
      localStorage.setItem('refreshToken', response.refresh_token || '')
      if (response.user_id) {
        webSocketService.connect(response.user_id)
      }

      return response
    } catch (responseError) {
      // Handle different error structures from api-client.js
      // For 403 errors: the full axios error object is rejected
      // For other errors: only error.response is rejected
      const status = responseError.status || responseError.response?.status
      const errorData = responseError.data || responseError.response?.data

      // Check if this is a server down error (no status at all)
      if (!status) {
        throw new ServerIsDown('Servidor fuera de servicio')
      }

      // For all errors, show the API error message
      throw new Error(errorData?.error || 'Error al iniciar sesión')
    } finally {
      isLoading.value = false
    }
  }

  // Helper function to handle OAuth errors (Google and Microsoft)
  const handleOAuthError = (responseError) => {
    // Check if this is a server down error (no status at all)
    if (!responseError.status && !responseError.response?.status && !responseError.data) {
      throw new ServerIsDown('Servidor fuera de servicio')
    }

    // Get status code and error data
    const statusCode = responseError.response?.status || responseError.status
    const errorData = responseError.data || responseError.response?.data

    // Handle 403 - Show error message from API
    if (statusCode === 403) {
      const error = new Error(errorData?.error || 'Error de autenticación')
      error.response = responseError.response
      error.status = statusCode
      throw error
    }

    // Handle 400 - Show error message from API
    if (statusCode === 400) {
      const error = new Error(errorData?.error || 'Error de solicitud')
      error.response = responseError.response
      error.status = statusCode
      throw error
    }

    // For all other errors, throw the original error
    throw responseError
  }

  // Google OAuth flow
  const startGoogleLogin = async () => {
    try {
      isLoading.value = true
      loginErrorMessage.value = null

      const response = await getGoogleOAuthUrl()

      if (response && response.auth_url) {
        // Redirect to Google OAuth URL
        window.location.href = response.auth_url
      } else {
        throw new Error('No auth URL received from backend')
      }
    } catch (error) {
      handleLoginError(error)
      isLoading.value = false
    }
  }

  // Handle Google OAuth callback
  const processGoogleCallback = async (authorizationCode) => {
    try {
      const response = await googleOAuthTokenValidation(authorizationCode)

      // Store auth token and connect websocket
      localStorage.setItem('accessToken', response.access_token)
      localStorage.setItem('refreshToken', response.refresh_token || '')
      if (response.user_id) {
        webSocketService.connect(response.user_id)
      }

      return response
    } catch (responseError) {
      handleOAuthError(responseError)
    }
  }

  // Microsoft OAuth flow
  const startMicrosoftLogin = async () => {
    try {
      isLoading.value = true
      loginErrorMessage.value = null

      const response = await getMicrosoftOAuthUrl()

      if (response && response.auth_url) {
        // Redirect to Microsoft OAuth URL
        window.location.href = response.auth_url
      } else {
        throw new Error('No auth URL received from backend')
      }
    } catch (error) {
      handleLoginError(error)
      isLoading.value = false
    }
  }

  // Handle Microsoft OAuth callback
  const processMicrosoftCallback = async (authorizationCode) => {
    try {
      const response = await microsoftOAuthTokenValidation(authorizationCode)

      // Store auth token and connect websocket
      localStorage.setItem('accessToken', response.access_token)
      localStorage.setItem('refreshToken', response.refresh_token || '')
      if (response.user_id) {
        webSocketService.connect(response.user_id)
      }

      return response
    } catch (responseError) {
      handleOAuthError(responseError)
    }
  }

  const handleLoginError = (error) => {
    if (error.name === 'ServerIsDown') {
      loginErrorMessage.value = 'Servidor fuera de servicio'
    } else {
      loginErrorMessage.value = error.message || 'Error al iniciar sesión'
    }
  }

  const logout = async () => {
    try {
      // First, notify the backend to invalidate the session
      await logoutService()
    } catch (error) {
      // Even if backend logout fails, continue with frontend cleanup
      console.warn('⚠️ Backend logout failed, continuing with frontend cleanup:', error.message)
    }

    // Clear frontend authentication state
    localStorage.setItem('accessToken', '')
    localStorage.setItem('refreshToken', '')
    clearSalesStatusShown()
    webSocketService.disconnect()

    // Limpiar usuario de Sentry
    Sentry.setUser(null)
  }

  const clearForceLogoutMessage = () => {
    forceLogoutMessage.value = null
  }

  const clearLoginError = () => {
    loginErrorMessage.value = null
  }

  // Check for force logout message from sessionStorage
  const checkForceLogoutMessage = () => {
    const forceLogoutReason = sessionStorage.getItem('force_logout_reason')
    if (forceLogoutReason) {
      forceLogoutMessage.value = forceLogoutReason
      sessionStorage.removeItem('force_logout_reason')
    }
  }

  const isLoggedIn = () => {
    return !!localStorage.getItem('accessToken')
  }

  return {
    // State
    isLoading,
    loginErrorMessage,
    errorMessage: loginErrorMessage, // Alias for Login.vue compatibility
    forceLogoutMessage,
    auth,
    isThereErrorLoginMessage,
    isThereForceLogoutMessage,

    // Methods
    login,
    performLogin,
    startGoogleLogin,
    processGoogleCallback,
    startMicrosoftLogin,
    processMicrosoftCallback,
    handleLoginError,
    logout,
    clearForceLogoutMessage,
    clearLoginError,
    checkForceLogoutMessage,
    isLoggedIn,

    // Centralized completion method
    completeLogin
  }
}



// WEBPACK FOOTER //
// ./src/app/authentication/compositions/UseAuthentication.js